// ============================================================================
// FinanceImportPage (E2b, F-505) — wizard CSV 2 etapes (analyze -> commit)
// ============================================================================
// Parite PoC /finance/import : le fichier n'est JAMAIS uploade tel quel —
// FileReader lit le TEXTE cote client, le CSV transite en memoire vers analyze
// puis commit (re-poste). Etats idle / preview / error / done. ADMIN seul
// (garde serveur) : un non-admin qui force l'URL obtient FinanceForbidden.

import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  PageHeader, EmptyState, ConfirmDialog, GsSegmented, GS_COMPONENTS, QueryError,
} from '@gs/ui';
import { ApiForbiddenError } from '@gs/api-client';
import { useAuth } from '@gs/auth';
import { useTranslation } from '../../i18n';
import {
  useFinanceImportAnalyze, useFinanceImportCommit,
  type ImportScenarioId, type FinanceImportAnalyzeResponse, type FinanceImportCommitResponse,
} from '../../hooks/useFinance';
import { FinanceForbidden } from './ResultatsFinanciersPage';

type WizardState = 'idle' | 'preview' | 'error' | 'done';

const SCENARIOS: ImportScenarioId[] = ['resultats', 'budget'];

export default function FinanceImportPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const analyze = useFinanceImportAnalyze();
  const commit = useFinanceImportCommit();

  const [scenario, setScenario] = useState<ImportScenarioId>('resultats');
  const [csv, setCsv] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [state, setState] = useState<WizardState>('idle');
  const [preview, setPreview] = useState<FinanceImportAnalyzeResponse | null>(null);
  const [done, setDone] = useState<FinanceImportCommitResponse | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const forbidden =
    (analyze.isError && analyze.error instanceof ApiForbiddenError)
    || (commit.isError && commit.error instanceof ApiForbiddenError);

  function reset() {
    setCsv(null);
    setFileName(null);
    setPreview(null);
    setDone(null);
    setErrorMsg(null);
    setState('idle');
    analyze.reset();
    commit.reset();
  }

  function onFile(file: File | null) {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const text = typeof reader.result === 'string' ? reader.result : '';
      setCsv(text);
      setFileName(file.name);
      setState('idle');
      setPreview(null);
      setErrorMsg(null);
    };
    reader.readAsText(file, 'UTF-8');
  }

  async function runAnalyze() {
    if (!csv || !user) return;
    setErrorMsg(null);
    try {
      const res = await analyze.mutateAsync({ scenario, csv, fileName: fileName ?? undefined });
      setPreview(res);
      setState('preview');
    } catch (e) {
      if (e instanceof ApiForbiddenError) return;
      setState('error');
      setErrorMsg(e instanceof Error ? e.message : t('gest:finance.import.errorGeneric'));
    }
  }

  async function runCommit() {
    if (!csv || !user) return;
    setConfirmOpen(false);
    setErrorMsg(null);
    try {
      const res = await commit.mutateAsync({ scenario, csv, fileName: fileName ?? undefined });
      setDone(res);
      setState('done');
    } catch (e) {
      if (e instanceof ApiForbiddenError) return;
      setState('error');
      setErrorMsg(e instanceof Error ? e.message : t('gest:finance.import.errorGeneric'));
    }
  }

  if (forbidden) {
    return (
      <div className="space-y-5">
        <PageHeader title={t('gest:finance.import.title')} />
        <FinanceForbidden />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <PageHeader
        title={t('gest:finance.import.title')}
        actions={(
          <Link to="/resultats-financiers/detail" className={GS_COMPONENTS.btnSecondary}>
            {t('gest:finance.import.backToDetail')}
          </Link>
        )}
      />

      <p className="text-field text-slate-600 dark:text-slate-300">
        {t('gest:finance.import.description')}
      </p>

      {state !== 'done' && (
        <>
          <GsSegmented
            ariaLabel={t('gest:finance.import.scenarioLabel')}
            value={scenario}
            onChange={(v) => {
              const next = v as ImportScenarioId;
              reset();
              setScenario(next);
            }}
            options={SCENARIOS.map((id) => ({
              value: id,
              label: t(`gest:finance.import.scenarios.${id}.label`),
            }))}
          />
          <p className="text-meta text-slate-500 dark:text-slate-400">
            {t(`gest:finance.import.scenarios.${scenario}.description`)}
          </p>
          <p className="text-meta text-slate-400 dark:text-slate-500">
            {t(`gest:finance.import.scenarios.${scenario}.fileHint`)}
          </p>

          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-xs dark:border-slate-700 dark:bg-slate-800">
            <label className="block text-field font-medium text-slate-700 dark:text-slate-200">
              {t('gest:finance.import.fileLabel')}
              <input
                type="file"
                accept=".csv,text/csv"
                className="mt-2 block w-full text-field"
                onChange={(e) => onFile(e.target.files?.[0] ?? null)}
              />
            </label>
            {fileName ? (
              <p className="mt-2 text-meta text-slate-500">{t('gest:finance.import.fileSelected', { name: fileName })}</p>
            ) : (
              <div className="mt-4">
                <EmptyState
                  title={t('gest:finance.import.empty.title')}
                  description={t('gest:finance.import.empty.description')}
                />
              </div>
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className={GS_COMPONENTS.btnAction}
              disabled={!csv || analyze.isPending}
              onClick={() => void runAnalyze()}
            >
              {analyze.isPending ? t('gest:finance.import.analyzing') : t('gest:finance.import.analyze')}
            </button>
          </div>
        </>
      )}

      {state === 'error' && errorMsg && (
        <QueryError message={errorMsg} onRetry={() => { setState('idle'); setErrorMsg(null); }} />
      )}

      {state === 'preview' && preview && (
        <section className="space-y-3 rounded-xl border border-slate-200 bg-white p-4 shadow-xs dark:border-slate-700 dark:bg-slate-800">
          <h2 className="text-card-title text-slate-800 dark:text-slate-100">
            {t('gest:finance.import.previewTitle')}
          </h2>
          <p className="text-field text-slate-600 dark:text-slate-300">
            {t('gest:finance.import.previewSummary', {
              n: preview.preview.lineCount,
              m: preview.preview.periods.length,
            })}
          </p>
          {preview.preview.warnings > 0 && (
            <p className="text-field text-amber-700 dark:text-amber-400" role="status">
              {t('gest:finance.import.reconWarnings', { n: preview.preview.warnings })}
            </p>
          )}
          {preview.budgetWarning && (
            <p className="text-field text-slate-500" role="status">{preview.budgetWarning}</p>
          )}
          <div className="overflow-x-auto">
            <table className="w-full min-w-max border-collapse text-field">
              <thead>
                <tr className="border-b border-slate-200 text-slate-500 dark:border-slate-700">
                  <th className="px-3 py-2 text-left">{t('gest:finance.period.label')}</th>
                  <th className="px-3 py-2 text-right">{t('gest:finance.kpi.revenue')}</th>
                  <th className="px-3 py-2 text-right">{t('gest:finance.kpi.expense')}</th>
                  <th className="px-3 py-2 text-right">{t('gest:finance.kpi.netIncome')}</th>
                  <th className="px-3 py-2 text-right">{t('gest:finance.import.deltaNet')}</th>
                  <th className="px-3 py-2 text-center">{t('gest:finance.import.reconOk')}</th>
                </tr>
              </thead>
              <tbody>
                {preview.preview.rows.map((r) => (
                  <tr key={r.period} className="border-b border-slate-100 dark:border-slate-700">
                    <td className="px-3 py-2">{r.period}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{r.revenue}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{r.expense}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{r.netComputed}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{r.deltaNet ?? '—'}</td>
                    <td className="px-3 py-2 text-center">{r.ok ? t('gest:finance.import.ok') : t('gest:finance.import.warn')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button
            type="button"
            className={GS_COMPONENTS.btnAction}
            disabled={commit.isPending}
            onClick={() => setConfirmOpen(true)}
          >
            {t('gest:finance.import.confirm')}
          </button>
        </section>
      )}

      {state === 'done' && done && (
        <section className="space-y-3 rounded-xl border border-slate-200 bg-white p-4 shadow-xs dark:border-slate-700 dark:bg-slate-800">
          <h2 className="text-card-title text-slate-800 dark:text-slate-100">
            {t('gest:finance.import.doneTitle')}
          </h2>
          <p className="text-field text-slate-600 dark:text-slate-300">
            {t('gest:finance.import.doneSummary', {
              n: done.written,
              m: done.periods.length,
            })}
          </p>
          <div className="flex flex-wrap gap-2">
            <Link to="/resultats-financiers/detail" className={GS_COMPONENTS.btnAction}>
              {t('gest:finance.import.backToDetail')}
            </Link>
            <button type="button" className={GS_COMPONENTS.btnSecondary} onClick={reset}>
              {t('gest:finance.import.another')}
            </button>
          </div>
        </section>
      )}

      <ConfirmDialog
        open={confirmOpen}
        variant="warning"
        title={t('gest:finance.import.confirmTitle')}
        message={t('gest:finance.import.confirmMessage', {
          n: preview?.preview.lineCount ?? 0,
          m: preview?.preview.periods.length ?? 0,
        })}
        confirmLabel={t('gest:finance.import.confirm')}
        cancelLabel={t('gest:finance.import.cancel')}
        isLoading={commit.isPending}
        onConfirm={() => void runCommit()}
        onCancel={() => setConfirmOpen(false)}
      />
    </div>
  );
}
